import { useState } from 'react';
import { toast } from 'sonner';
import { Check, CreditCard, Network, Plus } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Progress } from './ui/progress';

export const ChainSetupCard = ({ planned, added, plan, canEdit, onAdd, onChanged }) => {
  const [target, setTarget] = useState(planned);
  const done = added >= planned;
  const covered = plan.limit || 0;
  const needSlots = Math.max(0, planned - covered);

  const saveTarget = async (value) => {
    try {
      await api.put('/branches/setup-target', { planned_branches: value });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update your branch count'); }
  };

  return (
    <Card className="border-primary/30 bg-primary/5" data-testid="chain-setup-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><Network className="h-4 w-4 text-primary" /> Set up your chain</CardTitle>
        <CardDescription data-testid="chain-setup-progress-text">
          {done ? 'All your branches are added.' : `${added} of ${planned} locations added — your owner account manages them all.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Progress value={Math.min(100, (added / planned) * 100)} data-testid="chain-setup-progress" />
        <ol className="space-y-2">
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600" /> Head location created at signup</li>
          <li className="flex items-center gap-2" data-testid="chain-setup-step-branches">
            {done ? <Check className="h-4 w-4 text-emerald-600" /> : <Plus className="h-4 w-4 text-primary" />}
            Add your other {Math.max(0, planned - 1)} branch{planned - 1 === 1 ? '' : 'es'} — each one is reviewed by DutiLyne before it goes public
          </li>
          <li className="flex items-center gap-2" data-testid="chain-setup-step-billing">
            <CreditCard className="h-4 w-4 text-primary" />
            One bill: {plan.tier_name} covers {covered} location{covered === 1 ? '' : 's'}
            {needSlots > 0 ? `; ${needSlots} more at GHS ${plan.slot_price_ghs}/month each, on the same subscription` : ''}
          </li>
        </ol>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {!done && <Button size="sm" className="gap-1.5" onClick={onAdd} data-testid="chain-setup-add-branch"><Plus className="h-4 w-4" /> Add next branch</Button>}
            <Input type="number" min={1} max={50} value={target} className="h-9 w-20"
              onChange={(e) => setTarget(Math.min(50, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              data-testid="chain-setup-target-input" />
            <Button size="sm" variant="outline" onClick={() => saveTarget(target)} disabled={target === planned} data-testid="chain-setup-target-save">Update total</Button>
            {!done && <Button size="sm" variant="ghost" onClick={() => saveTarget(Math.max(1, added))} data-testid="chain-setup-finish">I'm done adding</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
